import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Popover,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueries } from 'react-query';
import { NumericFormat } from 'react-number-format';
import { useFormik } from 'formik';
import moment from 'moment';
import * as yup from 'yup';

import useTopPanel from 'hooks/useTopPanel';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useFormSubmit from 'hooks/useFormSubmit';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
import FormDateField from 'components/ui/form/FormDateField';
import FormSelectField from 'components/ui/form/FormSelectField';

// for react window //
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';
import ProductRowRWindow from './ProductRowRWindow';
// for react window //

const validationSchema = yup.object({
  supplier_id: yup.string().required('Yetkazib beruvchini tanlash majburiy!'),
  date: yup
    .date()
    .typeError("Sana noto'g'ri kiritilgan!")
    .required('Sana kiritish majburiy!'),
});

const itemKey = (index, data) => data?.items?.[index]?.id;

const WarehouseInbound = () => {
  const { setComponent } = useTopPanel();
  const { submit, isSubmitting } = useFormSubmit();
  const { id, inboundId } = useParams();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();

  const [hasError, setHasError] = useState(false);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState('');
  const [category, setCategory] = useState('');
  const [valuta, setValuta] = useState('');
  const [conversation, setConversation] = useState({ uzs: false });
  const [selectedValute, setSelectedValute] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);

  const formik = useFormik({
    initialValues: {
      supplier_id: '',
      date: moment().format('YYYY-MM-DD'),
    },
    validationSchema,
    onSubmit: async (values) => {
      const newValues = {
        ...values,
        is_conversion: conversation.uzs ? 1 : 0,
        conversion_rate: valuta || 0,
        converted_amount: uzsToUsdReduce || 0,
        products: products
          ?.filter((pr) => pr?.amount && pr?.price && !isNaN(pr?.isvalute))
          ?.map(({ id, isvalute, amount, price }) => {
            let sum = price;
            return {
              product_id: id,
              price: sum,
              isvalute,
              amount,
            };
          }),
      };

      const api = inboundId
        ? `/admin/warehouses/updateincome/${inboundId}`
        : `/admin/warehouses/income/${id}`;

      const navigateUrl = inboundId
        ? `/admin/warehouse/${id}/history`
        : '/admin/warehouse';

      submit(
        { type: 'post', contentType: 'simple' },
        newValues,
        api,
        values.name,
        id,
        true,
        () => navigate(navigateUrl)
      );
    },
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const idPopover = open ? 'simple-popover' : undefined;

  const [{ isLoading, isFetching }, { data }, { data: categories }] =
    useQueries([
      {
        queryKey: ['/dictionary/products', inboundId],
        queryFn: async function () {
          const response = await axiosPrivate.get(
            inboundId
              ? `/admin/warehouses/incomone/${inboundId}`
              : '/dictionary/products'
          );
          return response.data.data;
        },
        onSuccess: (data) => {
          if (data.length > 0) {
            if (inboundId) {
              const newData = data?.[0];

              setValuta(newData?.conversion_rate || '');
              setConversation({ uzs: !!newData?.is_conversion });

              formik.setValues({
                supplier_id: newData?.supplier_id || '',
                date: newData?.date || '',
              });

              const products = newData?.products?.map((d) => ({
                ...d,
                id: d?.product_id,
                name: d?.product?.name,
                unit: d?.product?.unit,
                category: d?.product?.category,
              }));
              setProducts(products);
            } else setProducts(data);
          }
        },
        enabled: !hasError,
        onError: (error) => {
          setHasError(true);
          // setBlocksError(true);
        },
        retry: false,
      },
      {
        queryKey: '/dictionary/valutes',
        queryFn: async function () {
          const response = await axiosPrivate.get('/dictionary/valutes');
          return response.data.data;
        },

        enabled: !hasError,
        onError: (error) => {
          setHasError(true);
          // setBlocksError(true);
        },
        retry: false,
      },
      {
        queryKey: '/dictionary/product-category',
        queryFn: async function () {
          const response = await axiosPrivate.get(
            '/dictionary/product-category'
          );
          return response.data.data;
        },

        enabled: !hasError,
        onError: (error) => {
          setHasError(true);
          // setBlocksError(true);
        },
        retry: false,
      },
    ]);

  useEffect(() => {
    setComponent(
      <form onSubmit={formik.handleSubmit}>
        <div className='component-title flex gap-4 items-center whitespace-nowrap'>
          <span>
            {inboundId
              ? 'Qabul qilinganlarni tahrirlash'
              : 'Omborga mahsulot qabul qilish'}
          </span>
          <div className='min-w-[200px]'>
            <FormDateField label='Sana' fieldName='date' formik={formik} />
          </div>
          <div className='min-w-[200px]'>
            <FormSelectField
              label='Yetkazib beruvchi'
              fieldName='supplier_id'
              formik={formik}
              path='/dictionary/suppliers'
            />
          </div>
          <div>
            <Button
              variant='contained'
              color='info'
              className='!ml-5'
              size='small'
              type='submit'
              startIcon={<i className='bi bi-box-arrow-in-down' />}
              disabled={
                isSubmitting ||
                !formik.values.supplier_id ||
                !products?.some((pr) => pr?.amount)
              }
            >
              Qabul qilish
            </Button>
          </div>
        </div>
      </form>
    );
  }, [products, isSubmitting, formik, formik.values]);

  const setValue = (id, field, value) => {
    if (id == 'all') {
      setProducts((prev) => prev.map((pr) => ({ ...pr, [field]: value })));
    } else
      setProducts((prev) =>
        prev?.map((pr) => (pr?.id == id ? { ...pr, [field]: value } : pr))
      );
  };

  const filteredData = useMemo(() => {
    if (!products.length) return [];

    let result = products;

    if (category) {
      result = result.filter((p) => p.category_id == category);
    }

    if (filter) {
      result = result.filter((p) =>
        p?.name?.toUpperCase()?.includes(filter?.toUpperCase())
      );
    }

    return result;
  }, [products, category, filter]);

  const sumReduce = useMemo(() => {
    if (!products.length) return 0;

    return products
      ?.filter((pr) => pr?.amount && pr?.price && pr?.isvalute == 0)
      ?.reduce((a, c) => a + c.amount * c.price, 0);
  }, [products]);

  const uzsToUsdReduce = useMemo(() => {
    if (!products.length || !valuta || !conversation.uzs) return 0;

    return products
      ?.filter((pr) => pr?.amount && pr?.price && pr?.isvalute == 0)
      ?.reduce((a, c) => a + (c.amount * c.price) / valuta, 0);
  }, [products, valuta, conversation.uzs]);

  const valutaReduce = useMemo(() => {
    if (!products.length) return 0;

    if (conversation.usd && valuta)
      return products
        ?.filter((pr) => pr?.amount && pr?.price && pr?.isvalute == 1)
        ?.reduce((a, c) => a + (c?.amount * c?.price) / valuta, 0);
    let result = products
      ?.filter((pr) => pr?.amount && pr?.price && pr?.isvalute == 1)
      ?.reduce((a, c) => a + c?.amount * c?.price, 0);

    return result;
  }, [products, valuta, conversation.usd]);

  return (
    <div className='component-list-wrapper'>
      <div className='component-table-wrapper h-[calc(100vh-80px)]'>
        <div className='calls-history-tab-item h-[calc(100vh-80px)]'>
          <TableContainer
            className='tab-table-container !shadow-none !base-border h-[calc(100vh-80px)]'
            component='div'
          >
            <Table stickyHeader component='div'>
              <TableHead component='div'>
                <Grid
                  container
                  component='div'
                  columns={{ xs: 12, sm: 12, lg: 12 }}
                  className={
                    'no-underline table-fixed pr-[15px] bg-gray-50 border-y border-t-transparent items-center'
                  }
                >
                  <Grid item xs={0.5} className='!bg-gray-50'>
                    <div className='font-semibold px-2 py-2'>#</div>
                  </Grid>
                  <Grid item xs={2.5} className='!bg-gray-50'>
                    <div className='font-semibold flex items-center gap-2 py-2 px-2'>
                      <span>Nomi</span>
                      <TextField
                        className='!m-0'
                        size='small'
                        placeholder='Qidiruv'
                        value={filter}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment
                              position='end'
                              className='custom-endAdornment'
                            >
                              {filter ? (
                                <IconButton
                                  onClick={() => setFilter('')}
                                  variant='onlyIcon'
                                >
                                  <i className='bi bi-x end-adornment-close-button' />
                                </IconButton>
                              ) : (
                                <IconButton
                                  variant='onlyIcon'
                                  disabled
                                  disableRipple
                                >
                                  <i className='bi bi-search text-base leading-4' />
                                </IconButton>
                              )}
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                        onChange={(e) => setFilter(e?.target?.value)}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={2}>
                    <div className='flex w-full items-center gap-2'>
                      <span>Kategoriyasi</span>
                      <div className='w-full max-w-[calc(100%-98px)]'>
                        <Select
                          fullWidth
                          onChange={(_, val) => {
                            setCategory(val?.props?.value);
                          }}
                          value={category}
                          size='small'
                          // className='max-w-[171px]'
                          endAdornment={
                            <InputAdornment
                              position='end'
                              className='custom-endAdornment'
                            >
                              {category && (
                                <IconButton
                                  onClick={() => {
                                    setValue('all', 'isvalute', null);
                                    setCategory(null);
                                  }}
                                  variant='onlyIcon'
                                  className='select-box-end-adornment-button'
                                >
                                  <i className='bi bi-x end-adornment-close-button' />
                                </IconButton>
                              )}
                            </InputAdornment>
                          }
                        >
                          {categories?.map((opt) => (
                            <MenuItem value={opt?.id} key={opt?.id}>
                              {opt?.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={1}>
                    <div className='font-semibold px-2 py-2'>Birlik</div>
                  </Grid>
                  <Grid item xs={2}>
                    <div className='font-semibold px-2 py-2'>Miqdori</div>
                  </Grid>
                  <Grid item xs={2}>
                    <div className='flex items-center gap-2 font-semibold px-2 py-2'>
                      Birlik narxi
                      <IconButton
                        aria-describedby={idPopover}
                        variant='contained'
                        size='small'
                        onClick={handleClick}
                      >
                        <i className='bi bi-three-dots-vertical' />
                      </IconButton>
                      <Popover
                        id={idPopover}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        PaperProps={{
                          className: 'p-6 !base-border !shadow-md',
                        }}
                      >
                        <div>
                          <label className='flex justify-between items-center mb-4 border border-gray-400 cursor-pointer pl-3 text-sm rounded-lg'>
                            <span className='mr-2 font-semibold select-none'>
                              UZSni konvertatsiya
                            </span>
                            <Switch
                              checked={conversation.uzs}
                              onChange={(e) =>
                                setConversation((prev) => ({
                                  ...prev,
                                  uzs: e.target.checked,
                                }))
                              }
                            />
                          </label>
                          <NumericFormat
                            label='Valyuta kursi'
                            onChange={(event) => {
                              const formattedValue =
                                event.target.value &&
                                parseFloat(
                                  event.target.value?.replaceAll(' ', '')
                                );

                              setValuta(formattedValue);
                            }}
                            fullWidth
                            value={valuta}
                            customInput={TextField}
                            allowNegative={false}
                            thousandSeparator={' '}
                            sx={{ m: 0, minWidth: 180 }}
                            decimalScale={3}
                          />
                        </div>
                      </Popover>
                      <span className='text-green-500 flex flex-wrap whitespace-nowrap gap-3'>
                        <CurrencyFormat value={sumReduce} suffix=' UZS' />
                        <CurrencyFormat value={valutaReduce} />
                        <br></br>
                        {conversation.uzs && valuta && (
                          <CurrencyFormat
                            value={uzsToUsdReduce}
                            suffix=' UZS → USD '
                          />
                        )}
                      </span>
                    </div>
                  </Grid>
                  <Grid item xs={2}>
                    <div className='flex items-center gap-2'>
                      <span>Valyuta</span>
                      <Select
                        fullWidth
                        onChange={(_, val) => {
                          setValue('all', 'isvalute', val?.props?.value);
                          setSelectedValute(val?.props?.value);
                        }}
                        value={selectedValute ?? ''}
                        size='small'
                        className='max-w-[calc(100%-60px)]'
                        endAdornment={
                          <InputAdornment
                            position='end'
                            className='custom-endAdornment'
                          >
                            {!!selectedValute && (
                              <IconButton
                                onClick={() => {
                                  setValue('all', 'isvalute', null);
                                  setSelectedValute('');
                                }}
                                variant='onlyIcon'
                                className='select-box-end-adornment-button'
                              >
                                <i className='bi bi-x end-adornment-close-button' />
                              </IconButton>
                            )}
                          </InputAdornment>
                        }
                      >
                        {data?.map((opt) => (
                          <MenuItem value={opt?.id} key={opt?.id}>
                            {opt?.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  </Grid>
                </Grid>
              </TableHead>
              {isLoading || isFetching ? (
                <TableBody className='overflow-hidden' component='div'>
                  <TableRow component='div'>
                    <TableCell colSpan={7}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : filteredData && filteredData?.length > 0 ? (
                <TableBody
                  className='overflow-hidden h-[calc(100vh-160px)] w-full'
                  component='div'
                >
                  <AutoSizer>
                    {({ height, width }) => (
                      <List
                        height={height}
                        width={width}
                        itemCount={filteredData.length}
                        itemSize={65}
                        itemKey={itemKey}
                        itemData={{
                          conversation,
                          valuta,
                          selectedValute,
                          listOfValuta: data,
                          items: filteredData,
                          onChange: setValue,
                        }}
                      >
                        {ProductRowRWindow}
                      </List>
                    )}
                  </AutoSizer>
                </TableBody>
              ) : (
                <TableBody component='div'>
                  <TableRow component='div'>
                    <TableCell colSpan={7}>
                      <span className='text-gray-400 flex items-center justify-center'>
                        <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                        Ma'lumot topilmadi!
                      </span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default memo(WarehouseInbound);
